import { useCallback, useMemo } from 'react'

import { intersection, isFunction } from 'lodash'
import { useSelector } from 'react-redux'

import { onError, onSuccess, resolvePromises } from '../../apiHelper'
import { createSectionGroup, deleteSectionGroup, deleteSectionGroupSections, getErrorTranslation, restoreSectionGroup, updateSectionGroup } from '../sectionGroup'
import { getConcurrentGroups, SectionGroup } from '../../entities/sectionGroup'
import { UpdateModes } from '../../enums/enums'
import { Actions } from '../../events'
import { selectUser } from '../../../reducers/UserReducer'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { getIdOrNull, isNonEmptyArray } from '../..'

const useSectionGroups = ({ callback, mode = UpdateModes.OPTIMISTIC, throwErrors = false }) => {
  const user = useSelector(selectUser)
  const t = useSelector(selectTranslate)

  const safeCallback = useMemo(() => isFunction(callback) ? callback : () => { }, [callback])

  const upsert = useCallback((sectionGroup, previousState) => {
    const config = sectionGroup.id === -1 ? { request: createSectionGroup, action: Actions.CREATE } : { request: updateSectionGroup, action: Actions.UPDATE }

    if (mode === UpdateModes.OPTIMISTIC) {
      safeCallback(sectionGroup, config.action)
    }

    return config.request(user, SectionGroup.toDTO(sectionGroup)).then(({ data }) => {
      const newData = new SectionGroup(data)

      if (config.action === Actions.CREATE) {
        if (mode === UpdateModes.OPTIMISTIC) {
          safeCallback(sectionGroup, Actions.DELETE, newData, Actions.CREATE)
        } else {
          safeCallback(newData, Actions.CREATE)
        }
      } else {
        safeCallback(newData, Actions.UPDATE)
      }

      onSuccess(t(`section_group.${config.action.toLowerCase()}.success`))
    }).catch(reason => {
      console.error(reason)

      if (mode === UpdateModes.OPTIMISTIC) {
        config.action === Actions.CREATE ? safeCallback(sectionGroup, Actions.DELETE) : safeCallback(previousState, Actions.UPDATE)
      }

      const error = `section_group.${config.action.toLowerCase()}.error`

      return reason.json().then(({ message }) => {
        onError(t(getErrorTranslation(message) ?? error))

        if (throwErrors) {
          throw new Error(message)
        }
      })
    }).catch(e => { throw e })
  }, [t, user, mode, throwErrors, callback])

  const del = useCallback(group => {
    return deleteSectionGroup(user, SectionGroup.toDTO(group)).then(({ data }) => {
      safeCallback(data, Actions.DELETE)

      onSuccess(t('section_group.delete.success'))
    }).catch(error => {
      console.error(error)
      onError(t('section_group.delete.error'))
    })
  }, [t, user, mode, callback])

  const transferSections = useCallback((sourceGroups, destination, sections) => {
    const sources = getConcurrentGroups(sourceGroups, destination, sections)

    const promises = []

    const toRollback = [destination.id]
    const toRestore = []

    sources.forEach(group => {
      const toRemove = intersection(sections.map(getIdOrNull), group.sections.map(getIdOrNull))

      if (isNonEmptyArray(toRemove)) {
        promises.push(deleteSectionGroupSections(user, SectionGroup.toDTO(group), toRemove))

        if (toRemove.length === group.sections.length) {
          toRestore.push(group.id)
        } else {
          toRollback.push(group.id)
        }
      }
    })

    return resolvePromises(promises)
      .then(() => {
        const updated = new SectionGroup({
          ...destination,
          sections: [...new Set(destination.sections.concat(sections))]
        })

        const config = updated.id === -1 ? { request: createSectionGroup, action: Actions.CREATE } : { request: updateSectionGroup, action: Actions.UPDATE }

        return config.request(user, updated.toRequestBody())
      })
      .then(() => onSuccess(t('section_group.transfer.success')))
      .catch(() => {
        const promises = []

        sourceGroups.filter(group => toRollback.includes(group.id)).forEach(group => {
          promises.push(updateSectionGroup(user, SectionGroup.toDTO(group)))
        })

        onError(t('section_group.transfer.error'))

        return resolvePromises(promises)
          .then(() => {
            const promises = []

            sourceGroups.filter(group => toRestore.includes(group.id)).forEach(group => {
              promises.push(restoreSectionGroup(user, group))
            })

            return resolvePromises(promises)
          })
          .then(() => onSuccess(t('section_group.transfert.restore.success')))
          .catch(() => onError(t('section_group.transfert.restore.error')))
      })
  }, [user, t])

  return { upsert, delete: del, transferSections }
}

export default useSectionGroups
